/**
 * Store Locator — Lifepro × Target Waver
 * Vanilla JS, no framework dependencies.
 * Google Maps JavaScript API loaded async after page render.
 */

// ─── Boot config ──────────────────────────────────────────────────────────────
const {
  storesUrl,
  zipCentroidsUrl,
  gmapsKey,
  productUrl,
  defaultRadius: configRadius
} = window.__storeLocator || {};

const PER_PAGE = 10;

// ─── State ────────────────────────────────────────────────────────────────────
let allStores = [];
let results = [];
let page = 0;
let origin = null;
let mapInstance = null;
let userMarker = null;
let storeMarkers = [];
let mapsReady = false;

// ─── DOM refs ─────────────────────────────────────────────────────────────────
const searchInput  = document.getElementById('sl-search-input');
const searchBtn    = document.getElementById('sl-search-btn');
const geoBtn       = document.getElementById('sl-geolocate');
const radiusSel    = document.getElementById('sl-radius');
const stateSel     = document.getElementById('sl-state');
const tabBar       = document.getElementById('sl-tab-bar');
const body         = document.getElementById('sl-body');
const listPanel    = document.getElementById('sl-list-panel');
const mapPanel     = document.getElementById('sl-map-panel');
const cardsEl      = document.getElementById('sl-cards');
const countEl      = document.getElementById('sl-count');
const emptyEl      = document.getElementById('sl-empty');
const emptyMsgEl   = document.getElementById('sl-empty-msg');
const targetFallback = document.getElementById('sl-target-fallback');
const errorEl      = document.getElementById('sl-error');
const loadMoreBtn  = document.getElementById('sl-load-more');
const mapEl        = document.getElementById('sl-map');
const mapFallback  = document.getElementById('sl-map-fallback');

// ─── Helpers ──────────────────────────────────────────────────────────────────
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isZipCode(s) {
  return /^\d{5}(-\d{4})?$/.test(s.trim());
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getRadius() {
  return parseInt(radiusSel.value, 10);
}

// ─── Store data ───────────────────────────────────────────────────────────────
async function loadStores() {
  const res = await fetch(storesUrl);
  allStores = await res.json();
  populateStates();
}

function populateStates() {
  const states = [...new Set(allStores.map(s => s.state))].sort();
  states.forEach(st => {
    const opt = document.createElement('option');
    opt.value = st;
    opt.textContent = st;
    stateSel.appendChild(opt);
  });
}

// ─── Search core ──────────────────────────────────────────────────────────────
function filterByDistance(lat, lng, radiusMi) {
  const stateFilter = stateSel.value;
  return allStores
    .filter(s => s.lat != null && s.lng != null)
    .filter(s => !stateFilter || s.state === stateFilter)
    .map(s => ({ ...s, distance: haversineDistance(lat, lng, s.lat, s.lng) }))
    .filter(s => s.distance <= radiusMi)
    .sort((a, b) => a.distance - b.distance);
}

function searchByCoords(lat, lng, radiusMi) {
  origin = { lat, lng };
  results = filterByDistance(lat, lng, radiusMi);
  presentResults('geolocation', 'your location');
}

// ─── ZIP centroid lookup (no API key required) ────────────────────────────────
let zipCentroids = null;

async function getZipCentroids() {
  if (zipCentroids !== null) return zipCentroids;
  const res = await fetch(zipCentroidsUrl);
  zipCentroids = await res.json();
  return zipCentroids;
}

async function lookupZip(zip) {
  const data = await getZipCentroids();
  return data[zip] || null; // [lat, lng] or null
}

async function searchByZip(zip, radiusMi) {
  clearMessages();
  const clean = zip.replace(/\D/g, '').slice(0, 5);
  if (clean.length !== 5) {
    showError('Please enter a valid 5-digit US zip code.');
    return;
  }
  const coords = await lookupZip(clean);
  if (!coords) {
    showError(`Zip code ${clean} was not recognized. Please check and try again.`);
    return;
  }
  origin = { lat: coords[0], lng: coords[1] };
  results = filterByDistance(origin.lat, origin.lng, radiusMi);
  presentResults('zip', clean);
}

async function searchByCity(city, stateCode, radiusMi) {
  clearMessages();

  // Check store data for city name ambiguity across states
  const cityLower = city.toLowerCase().trim();
  const matchingStates = [
    ...new Set(
      allStores
        .filter(s => s.city.toLowerCase() === cityLower)
        .map(s => s.state)
    )
  ];

  if (matchingStates.length > 1 && !stateCode) {
    showDisambiguation(city, matchingStates);
    return;
  }

  const query = stateCode ? `${city}, ${stateCode}, USA` : `${city}, USA`;
  const data = await geocodeQuery(query);
  if (!data || !data.results || !data.results.length) {
    results = [];
    showNoResults('city', city, radiusMi);
    trackNoResults('city', city, radiusMi);
    return;
  }
  const { lat, lng } = data.results[0].geometry.location;
  origin = { lat, lng };
  results = filterByDistance(lat, lng, radiusMi);
  presentResults('city', stateCode ? `${city}, ${stateCode}` : city);
}

async function geocodeQuery(address) {
  if (!gmapsKey) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${gmapsKey}`;
    const res = await fetch(url);
    return await res.json();
  } catch (e) {
    return null;
  }
}

// ─── Present results ──────────────────────────────────────────────────────────
function presentResults(searchType, label) {
  clearMessages();
  page = 0;

  if (results.length === 0) {
    showNoResults(searchType, label, getRadius());
    trackNoResults(searchType, label, getRadius());
    return;
  }

  const shown = Math.min(PER_PAGE, results.length);
  countEl.textContent = `Showing ${shown} of ${results.length} store${results.length !== 1 ? 's' : ''} near ${label}`;
  countEl.focus();

  renderCards();
  renderPins(results, origin);
  trackSearch(searchType, results.length);
}

// ─── Card rendering ───────────────────────────────────────────────────────────
function cardHtml(store) {
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(
    `${store.address}, ${store.city}, ${store.state} ${store.zip}`
  )}`;
  const tUrl = productUrl || '#';
  const dist = store.distance != null
    ? `${store.distance.toFixed(1)} miles away`
    : '';

  return `
    <div class="store-locator__card" role="listitem" data-store-id="${store.id}">
      <div class="store-locator__card-inner">
        <div class="store-locator__card-info">
          <h3 class="store-locator__card-name">Target &mdash; ${escHtml(store.name)}</h3>
          <address class="store-locator__card-address">
            ${escHtml(store.address)}<br>
            ${escHtml(store.city)}, ${escHtml(store.state)} ${escHtml(store.zip)}
          </address>
          ${dist ? `<p class="store-locator__card-dist">${dist}</p>` : ''}
        </div>
        <div class="store-locator__card-actions">
          <a href="${mapsUrl}"
             target="_blank" rel="noopener noreferrer"
             class="store-locator__card-btn btn--outline store-locator__card-btn--directions"
             aria-label="Get directions to Target ${escHtml(store.name)}"
             data-track-cta="directions">
            Get Directions
          </a>
          <a href="${escHtml(tUrl)}"
             target="_blank" rel="noopener noreferrer"
             class="store-locator__card-btn btn--primary store-locator__card-btn--target"
             aria-label="View LifePro Waver at Target.com"
             data-track-cta="target_url">
            View on Target.com
          </a>
        </div>
      </div>
    </div>`;
}

function wireCard(card) {
  const id = parseInt(card.dataset.storeId, 10);
  card.addEventListener('mouseenter', () => highlightPin(id));
  card.addEventListener('focusin', () => highlightPin(id));
  // Card click → bounce the corresponding map pin (reverse of pin→card)
  card.addEventListener('click', () => activatePin(id));
  card.querySelectorAll('[data-track-cta]').forEach(btn => {
    btn.addEventListener('click', () => {
      const store = results.find(s => s.id === id);
      if (store) trackResultClick(store, btn.dataset.trackCta);
    });
  });
}

function renderCards() {
  cardsEl.innerHTML = '';
  results.slice(0, PER_PAGE).forEach(s => {
    cardsEl.insertAdjacentHTML('beforeend', cardHtml(s));
    wireCard(cardsEl.lastElementChild);
  });
  loadMoreBtn.hidden = results.length <= PER_PAGE;
}

function paginate() {
  page++;
  const start = page * PER_PAGE;
  const end = Math.min(start + PER_PAGE, results.length);
  results.slice(start, end).forEach(s => {
    cardsEl.insertAdjacentHTML('beforeend', cardHtml(s));
    wireCard(cardsEl.lastElementChild);
  });
  loadMoreBtn.hidden = end >= results.length;
  const shown = end;
  countEl.textContent = `Showing ${shown} of ${results.length} stores`;
}

// ─── Empty / error states ─────────────────────────────────────────────────────
function showNoResults(type, label, radius) {
  cardsEl.innerHTML = '';
  loadMoreBtn.hidden = true;
  countEl.textContent = '';

  if (radius < 100) {
    emptyMsgEl.textContent = `No stores found within ${radius} miles of "${label}". Try expanding your search radius.`;
  } else {
    emptyMsgEl.textContent = `No stores found within 100 miles of "${label}". The Waver is available at Target.com.`;
  }

  if (targetFallback) {
    targetFallback.href = productUrl || '#';
  }
  emptyEl.hidden = false;
}

function showError(msg) {
  clearMessages();
  errorEl.textContent = msg;
  errorEl.hidden = false;
}

function showDisambiguation(city, states) {
  clearMessages();
  cardsEl.innerHTML = '';
  countEl.textContent = '';
  loadMoreBtn.hidden = true;

  const html = `
    <p>We found "${escHtml(city)}" in multiple states. Which one did you mean?</p>
    <div class="store-locator__disambig">
      ${states.map(st => `
        <button type="button" class="store-locator__disambig-btn btn--outline"
                data-city="${escHtml(city)}" data-state="${escHtml(st)}">
          ${escHtml(city)}, ${escHtml(st)}
        </button>`).join('')}
    </div>`;

  errorEl.innerHTML = html;
  errorEl.hidden = false;

  errorEl.querySelectorAll('.store-locator__disambig-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      errorEl.hidden = true;
      searchByCity(btn.dataset.city, btn.dataset.state, getRadius());
    });
  });
}

function clearMessages() {
  emptyEl.hidden = true;
  errorEl.hidden = true;
  errorEl.innerHTML = '';
}

// ─── Mobile tabs ──────────────────────────────────────────────────────────────
function setupTabs() {
  tabBar.querySelectorAll('.store-locator__tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      tabBar.querySelectorAll('.store-locator__tab').forEach(t => {
        const active = t.dataset.tab === target;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active);
      });
      body.dataset.activeTab = target;
    });
  });
}

// ─── Google Maps ──────────────────────────────────────────────────────────────
function loadGoogleMaps() {
  if (!gmapsKey) {
    mapFallback.hidden = false;
    mapEl.style.display = 'none';
    return;
  }

  window.__slMapReady = function () {
    mapsReady = true;
    initMap();
    // Render pins if a search already fired (URL params race condition)
    if (results.length > 0) {
      renderPins(results, origin);
    }
  };

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${gmapsKey}&callback=__slMapReady&loading=async`;
  script.async = true;
  script.defer = true;
  script.onerror = () => {
    mapFallback.hidden = false;
    mapEl.style.display = 'none';
  };
  document.head.appendChild(script);
}

function initMap() {
  if (!window.google) return;
  mapInstance = new window.google.maps.Map(mapEl, {
    center: { lat: 39.5, lng: -98.35 },
    zoom: 4,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative'
  });
}

function renderPins(stores, userCoords) {
  if (!mapsReady || !mapInstance) return;

  // Clear existing markers
  storeMarkers.forEach(m => m.setMap(null));
  storeMarkers = [];
  if (userMarker) {
    userMarker.setMap(null);
    userMarker = null;
  }

  const bounds = new window.google.maps.LatLngBounds();

  if (userCoords) {
    userMarker = new window.google.maps.Marker({
      position: userCoords,
      map: mapInstance,
      title: 'Your location',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#4285F4',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2
      },
      zIndex: 1000
    });
    bounds.extend(userCoords);
  }

  stores.forEach(store => {
    if (store.lat == null || store.lng == null) return;
    const marker = new window.google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      map: mapInstance,
      title: `Target — ${store.name}`
    });

    window.google.maps.event.addListener(marker, 'click', function() {
      activatePin(store.id);
      scrollToCard(store.id);
    });

    marker.storeId = store.id;
    storeMarkers.push(marker);
    bounds.extend({ lat: store.lat, lng: store.lng });
  });

  if (!bounds.isEmpty()) {
    mapInstance.fitBounds(bounds, { padding: 60 });
    // Don't zoom in past street level for a single result
    const listener = window.google.maps.event.addListener(mapInstance, 'bounds_changed', () => {
      if (mapInstance.getZoom() > 14) mapInstance.setZoom(14);
      window.google.maps.event.removeListener(listener);
    });
  }
}

// Lightweight highlight — used by mouseenter/focusin (no animation noise on hover)
function highlightPin(storeId) {
  storeMarkers.forEach(m => {
    m.setZIndex(m.storeId === storeId ? 999 : 1);
  });
  document.querySelectorAll('.store-locator__card').forEach(card => {
    card.classList.toggle('is-highlighted', parseInt(card.dataset.storeId, 10) === storeId);
  });
}

// Full activation — used by pin click and card click (bounces the pin)
function activatePin(storeId) {
  // Stop any in-progress bounce on all markers, reset z-index
  storeMarkers.forEach(m => {
    if (m.getAnimation() !== null) m.setAnimation(null);
    m.setZIndex(m.storeId === storeId ? 999 : 1);
  });

  // Bounce the target pin for 750 ms
  const target = storeMarkers.find(m => m.storeId === storeId);
  if (target) {
    target.setAnimation(window.google.maps.Animation.BOUNCE);
    setTimeout(function() { target.setAnimation(null); }, 750);
  }

  // Remove highlight from all cards
  document.querySelectorAll('.store-locator__card').forEach(function(c) {
    c.classList.remove('is-highlighted');
  });

  // Highlight matching card; on desktop scroll it into view
  var card = document.querySelector('.store-locator__card[data-store-id="' + storeId + '"]');
  if (card) {
    card.classList.add('is-highlighted');
    if (window.innerWidth >= 1024) {
      requestAnimationFrame(function() {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }

  // On mobile show the map card popup below the map instead of switching tabs
  if (window.innerWidth < 1024) {
    var mapCard = document.getElementById('sl-map-card');
    var mapCardContent = document.getElementById('sl-map-card-content');
    if (mapCard && mapCardContent) {
      var store = results.find(function(s) { return s.id === storeId; });
      if (store) {
        var dist = store.distance != null ? store.distance.toFixed(1) + ' miles away' : '';
        var tUrl = productUrl || '#';
        var directionsUrl = 'https://maps.google.com/?q=' + encodeURIComponent(
          store.address + ', ' + store.city + ', ' + store.state + ' ' + store.zip
        );
        mapCardContent.innerHTML = `
          <div class="store-locator__card-name">Target — ${escHtml(store.name)}</div>
          <address class="store-locator__card-address">${escHtml(store.address)}<br>${escHtml(store.city)}, ${escHtml(store.state)} ${escHtml(store.zip)}</address>
          ${dist ? `<p class="store-locator__card-dist">${dist}</p>` : ''}
          <div class="store-locator__card-actions">
            <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer"
               class="store-locator__card-btn btn--secondary store-locator__card-btn--directions">Get Directions</a>
            <a href="${escHtml(tUrl)}" target="_blank" rel="noopener noreferrer"
               class="store-locator__card-btn btn--outline store-locator__card-btn--target">View on Target.com</a>
          </div>`;
        mapCard.removeAttribute('hidden');
        mapCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
}

function scrollToCard(storeId) {
  const card = document.querySelector(`.store-locator__card[data-store-id="${storeId}"]`);
  if (!card) return;

  // On mobile the map tab is active when a pin is clicked — switch to list
  // first so the panel is visible before scrollIntoView fires.
  if (window.innerWidth < 1024) {
    const listTab = tabBar.querySelector('[data-tab="list"]');
    if (listTab && !listTab.classList.contains('is-active')) {
      listTab.click();
    }
  }

  // Defer scroll one frame so the panel visibility change has been painted.
  requestAnimationFrame(() => {
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
}

// ─── Analytics ────────────────────────────────────────────────────────────────
function trackSearch(type, count) {
  if (typeof gtag !== 'function') return;
  gtag('event', 'store_locator_search', { search_type: type, results_count: count });
}

function trackResultClick(store, ctaType) {
  if (typeof gtag !== 'function') return;
  gtag('event', 'store_locator_result_click', {
    store_id: store.id,
    store_name: `Target — ${store.name}`,
    distance_miles: store.distance != null ? parseFloat(store.distance.toFixed(1)) : null,
    cta_type: ctaType
  });
}

function trackNoResults(type, term, radius) {
  if (typeof gtag !== 'function') return;
  gtag('event', 'store_locator_no_results', {
    search_type: type,
    search_term: term,
    radius_miles: radius
  });
}

// ─── URL params ───────────────────────────────────────────────────────────────
function handleUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const zip    = params.get('zip');
  const city   = params.get('city');
  const state  = params.get('state');
  const lat    = params.get('lat');
  const lng    = params.get('lng');

  if (zip && zip.trim()) {
    searchInput.value = zip.trim();
    searchByZip(zip.trim(), getRadius());
  } else if (lat && lng) {
    const la = parseFloat(lat);
    const ln = parseFloat(lng);
    if (!isNaN(la) && !isNaN(ln)) {
      searchByCoords(la, ln, getRadius());
    }
  } else if (city && city.trim()) {
    searchInput.value = state ? `${city.trim()}, ${state.trim()}` : city.trim();
    searchByCity(city.trim(), state ? state.trim().toUpperCase() : null, getRadius());
  }
}

// ─── Search dispatch ──────────────────────────────────────────────────────────
function triggerSearch() {
  const val = searchInput.value.trim();
  if (!val) return;

  if (isZipCode(val)) {
    searchByZip(val, getRadius());
    return;
  }

  // "City, ST" pattern
  const match = val.match(/^(.+),\s*([A-Za-z]{2})$/);
  if (match) {
    searchByCity(match[1].trim(), match[2].toUpperCase(), getRadius());
    return;
  }

  searchByCity(val, null, getRadius());
}

// ─── Init ─────────────────────────────────────────────────────────────────────
async function init() {
  // Apply default radius from theme settings
  if (configRadius) {
    radiusSel.value = String(configRadius);
  }

  await loadStores();

  loadGoogleMaps();
  setupTabs();

  searchBtn.addEventListener('click', triggerSearch);
  searchInput.addEventListener('keydown', e => {
    if (e.key === 'Enter') triggerSearch();
  });

  geoBtn.addEventListener('click', () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        searchByCoords(pos.coords.latitude, pos.coords.longitude, getRadius());
      },
      () => {
        // Denied or unavailable — silent fallback
        searchInput.focus();
      },
      { timeout: 8000 }
    );
  });

  loadMoreBtn.addEventListener('click', paginate);

  document.getElementById('sl-map-card-close')?.addEventListener('click', () => {
    document.getElementById('sl-map-card')?.setAttribute('hidden', '');
  });

  handleUrlParams();
}

document.addEventListener('DOMContentLoaded', init);
